import type { BeforeSendFn, CaptureResult, Properties } from "posthog-js";

type ExceptionEntry = {
  type?: string;
  value?: string;
  frameCount: number | null;
};

export function isNoiseException(event: CaptureResult | null): boolean {
  if (event?.event !== "$exception") return false;

  const entries = readExceptions(event.properties);
  if (!entries) return false;

  return entries.every(
    (entry) =>
      (isScriptError(entry) || isFailedFetch(entry)) && isStackless(entry),
  );
}

export const dropNoiseExceptions: BeforeSendFn = (event) => {
  if (isNoiseException(event)) return null;
  return event;
};

function readExceptions(properties: Properties): ExceptionEntry[] | null {
  const list = properties.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return null;

  const entries: ExceptionEntry[] = [];
  for (const item of list) {
    if (!isRecord(item)) return null;
    entries.push({
      type: typeof item.type === "string" ? item.type : undefined,
      value: typeof item.value === "string" ? item.value : undefined,
      frameCount: frameCount(item.stacktrace),
    });
  }
  return entries;
}

function isScriptError(entry: ExceptionEntry): boolean {
  return entry.value === "Script error.";
}

function isFailedFetch(entry: ExceptionEntry): boolean {
  return entry.type === "TypeError" && entry.value === "Failed to fetch";
}

function isStackless(entry: ExceptionEntry): boolean {
  return entry.frameCount === null || entry.frameCount === 0;
}

function frameCount(stacktrace: unknown): number | null {
  if (!isRecord(stacktrace) || !Array.isArray(stacktrace.frames)) return null;
  return stacktrace.frames.length;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
